<template>
    <v-form v-model="formIsValid">
        <v-row no-gutters>
            <v-col cols="8" class="text-subtitle-1 px-3 pt-2"> Les acomptes de la facture </v-col>
            <v-col cols="4" class="px-3 pt-2">
                <v-switch
                    v-model="displayPercentageAndDisableAmount"
                    hide-details
                    label="En pourcentage"
                    class="my-0 py-0"
                />
            </v-col>
            <v-col cols="12" class="px-3 pt-2">
                <v-row
                    no-gutters
                    v-for="(depo, index) of newDeposits"
                    :key="index + '_deposit'"
                    class="d-flex justify-center pb-3"
                >
                    <!-- <v-col cols="1" class="d-flex align-center justify-center">
                        {{ index === 0 ? "" : "ET" }}
                    </v-col> -->
                    <v-col cols="4">
                        <v-text-field-filled
                            v-model="depo.name"
                            label="Nom de l'acompte"
                            :rules="[rules.notEmpty]"
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="3" class="pl-2">
                        <oss-date-picker-filled
                            v-model="depo.date"
                            label="Date"
                            :rules="[rules.notEmpty]"
                        >
                        </oss-date-picker-filled>
                    </v-col>
                    <v-col cols="2" class="pl-2" v-if="displayPercentageAndDisableAmount">
                        <v-text-field-filled
                            v-model="depo.percentage"
                            :text="limit2decimals(depo.percentage)"
                            type="number"
                            suffix="%"
                            :rules="[rules.notEmpty, rules.isPercentage]"
                            @update:model-value="adaptDepositLine('amount', index)"
                            density="compact"
                        />
                    </v-col>
                    <v-col :cols="displayPercentageAndDisableAmount ? '2' : '4'" class="pl-2">
                        <oss-text-field-filled
                            v-model="depo.amount"
                            :text="limit2decimals(depo.amount)"
                            outlined
                            dense
                            suffix="&#x20ac;"
                            :rules="[rules.notEmpty, rules.isCorrectAmount]"
                            :disabled="displayPercentageAndDisableAmount"
                            @change="adaptDepositLine('percentage', index)"
                        />
                    </v-col>
                    <v-col col="1" class="d-flex align-center justify-center">
                        <v-btn-trash
                            v-if="newDeposits.length > 2"
                            @click="removeDepositLine(index)"
                            size="small"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="d-flex justify-end">
            <v-col cols="3" class="py-2">
                <v-btn size="small" elevation="0" color="primary" @click="addDepositLine()">
                    Ajouter un acompte
                    <v-icon icon="$add" class="ml-2" />
                </v-btn>
            </v-col>
            <!-- <v-col cols="1"></v-col> -->
        </v-row>
        <v-row no-gutters class="pt-3 d-flex justify-end">
            <v-col cols="3">
                <div class="totals text-right">
                    <span>Total des acomptes</span>
                </div>
                <div class="totals text-right" style="font-weight: normal">
                    <span>Total de la facture</span>
                </div>
                <div class="totals text-right">
                    <span>Différence</span>
                </div>
            </v-col>
            <v-col cols="3">
                <div class="totals text-right">
                    <span>{{ newCommonFormatCurrency(depositsTotal, undefined) }}</span>
                </div>
                <div class="totals text-right" style="font-weight: normal">
                    <span>{{ newCommonFormatCurrency(grossAmountTotal, undefined) }}</span>
                </div>
                <div class="totals text-right" :style="diffDepositAndGrossAmountStyle">
                    <!-- demander Romain pb réactivité -->
                    <span>{{
                        newCommonFormatCurrency(diffDepositAndGrossAmountTotals, undefined)
                    }}</span>
                </div>
            </v-col>
            <v-col cols="1" />
        </v-row>
    </v-form>
</template>

<script lang="ts">
import moment from "moment-timezone";
import { mathFix } from "../../services/mathServices";
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { InvoiceDTO } from "../../types/invoices.types";
import { Prop } from "vue-facing-decorator";
import { Watch, Model } from "vue-facing-decorator";

@Component({
    emits: ["newDeposits-updated", "isValid-updated"],
})
export class CreateDeposit extends Vue {
    @Prop() invoice: InvoiceDTO;
    @Prop() grossAmountTotal: number;
    @Prop({ default: true }) fromQuotation: boolean;
    @Model({ required: true, type: Boolean }) depositIsValid: boolean;

    //! new deposits is NOT only for new depo lines
    newDeposits: Array<{ name: string; date: string; amount: number; percentage: number }> = [
        { name: "", date: "", amount: 0.0, percentage: 0 },
    ];
    displayPercentageAndDisableAmount = false;
    formIsValid: boolean = false;

    get rules() {
        return {
            notEmpty: (value: string): string | boolean => {
                if (!value) {
                    return "A remplir";
                }
                return true;
            },
            isPercentage: (value: string): string | boolean => {
                if (!value) return true;
                // if (!checkFloat(value)) return "Montant invalide"
                if (parseFloat(value) >= 0 && parseFloat(value) <= 100) {
                    return true;
                }
                console.log(typeof value);
                return "Pourcentage invalide";
            },
            isCorrectAmount: (value: string): boolean | string => {
                if (!value) return true;
                const regExpAmount = new RegExp(/^\d+([,.]\d{1,2})?$/);
                if (regExpAmount.test(value)) {
                    const value_ = `${value}`;
                    const floatedValue = parseFloat(value_.replace(",", "."));
                    if (floatedValue >= 0 && floatedValue <= this.grossAmountTotal) {
                        return true;
                    }
                    if (floatedValue > this.grossAmountTotal) {
                        return "Ne peut pas dépasser le total de la facture";
                    }
                }
                return "Montant invalide";
            },
        };
    }

    get isValid(): boolean {
        // question Eric : que faire si perte de micro centimes ?
        return this.formIsValid && Math.abs(this.diffDepositAndGrossAmountTotals) < 0.01;
    }

    get depositsTotal(): number {
        let result = 0;
        for (const d of this.newDeposits) {
            result += +d.amount;
        }
        return result;
    }

    get diffDepositAndGrossAmountTotals(): number {
        return this.depositsTotal - this.grossAmountTotal;
    }

    get diffDepositAndGrossAmountStyle(): string {
        return "color:" + (Math.abs(this.diffDepositAndGrossAmountTotals) >= 0.01 ? "red" : "");
    }

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, options);
    }

    get lastDepositLineDate(): string {
        if (!this.newDeposits?.length || this.newDeposits.length === 1) {
            return moment(this.invoice.date).add(1, "months").format();
        } else {
            return moment(this.newDeposits[this.newDeposits.length - 1].date ?? "")
                .add(1, "months")
                .format();
        }
    }

    async beforeMount() {
        if (this.invoice?.data?.deposits) {
            this.newDeposits.splice(0, this.newDeposits.length);
            this.invoice.data.deposits.forEach((dep: any) => {
                this.newDeposits.push({ ...dep });
            });
        } else if (this.newDeposits.length === 1) {
            // newDeposits.length >= 2 normally => initiate at 1 to call this function 1st time
            const roundedHalfGrossAmount = mathFix(this.grossAmountTotal / 2); // Juan : Je crois que c'est pas bon de faire ça comme ca
            this.newDeposits = [
                {
                    name:
                        "Acompte 1 " +
                        (this.fromQuotation
                            ? "du " + this.invoice.number
                            : "de la {{FACTURE-PROVISOIRE}}"),
                    date: moment(this.invoice.date).format(), //.toDate(),
                    amount: roundedHalfGrossAmount,
                    percentage: 50,
                },
                {
                    name:
                        "Acompte 2 " +
                        (this.fromQuotation
                            ? "du " + this.invoice.number
                            : "de la {{FACTURE-PROVISOIRE}}"),
                    date: this.lastDepositLineDate,
                    amount: this.grossAmountTotal - roundedHalfGrossAmount,
                    percentage: 50,
                },
            ];
        }
        console.log("this.newDeposits after", this.newDeposits);
    }

    @Watch("newDeposits", { deep: true })
    emitFatherNewDeposits() {
        this.$emit("newDeposits-updated", this.newDeposits);
    }

    @Watch("isValid")
    emitFatherIsValid() {
        this.depositIsValid = this.isValid;
    }

    @Watch("grossAmountTotal", { deep: true })
    adaptAmountIfPercentage() {
        for (const depoIndex in this.newDeposits) {
            if (this.displayPercentageAndDisableAmount) {
                this.adaptDepositLine("amount", parseInt(depoIndex));
            } else {
                this.adaptDepositLine("percentage", parseInt(depoIndex));
            }
        }
    }

    adaptDepositLine(type: "amount" | "percentage", index: number) {
        const depo = this.newDeposits[index];
        if (type === "amount") {
            depo.amount = Math.round(this.grossAmountTotal * (depo.percentage / 100) * 100) / 100;
        } else if (type === "percentage") {
            depo.percentage = Math.round((depo.amount / this.grossAmountTotal) * 100 * 100) / 100;
        }
    }

    removeDepositLine(index: number): void {
        this.newDeposits.splice(index, 1);
    }

    addDepositLine(): void {
        console.log("this.newDeposits", this.newDeposits);
        const newAmount = -Math.round(this.diffDepositAndGrossAmountTotals * 100) / 100;
        const newPercentage = Math.round((newAmount / this.grossAmountTotal) * 100 * 100) / 100;
        // const partName = this.displayPercentageAndDisableAmount ? newPercentage + "%" : newCommonFormatCurrency(newAmount)
        const newName = this.newName();
        this.newDeposits.push({
            name: newName,
            date: this.lastDepositLineDate,
            amount: newAmount,
            percentage: newPercentage,
        });

        console.log("this.newDeposits after push", this.newDeposits);
    }

    newName(): string {
        let result = "Acompte " + (this.newDeposits.length + 1);
        if (this.fromQuotation) {
            result += " du devis " + this.invoice.number;
        } else {
            result += " de la {{FACTURE-PROVISOIRE}}";
        }
        return result;
    }

    //* Inherite from InvoiceEdition  --- extends component from InvoiceEdition ?

    mathFixString(amount: number): string {
        return mathFix(amount, this.maxDeciamalAmount).toString().replace(".", ",") + " €";
    }
    get maxDeciamalAmount(): number {
        let max = 0;
        this.invoice.lines.forEach((line) => {
            const decimals = line?.unitPrice?.toString().split(".")?.[1]?.length;
            if (decimals > max) {
                max = decimals;
            }
        });
        max = Math.max(max, 2);
        return max % 2 === 0 ? max : max + 1;
    }

    limit2decimals(percentage): number {
        return mathFix(percentage, 2);
    }
}

export default toNative(CreateDeposit);
</script>
